import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BaseUrls } from '@app/app.constants';
import { EndUserWithId } from '@app/core/interfaces/c5/end-user';
import { RoomGroup } from '@app/core/interfaces/c5/room-group';
import { UserLevel } from '@app/core/models/user-level';
import { EnhancedEndUser } from '@app/shared/models/enhanced-end-user';
import { Utils } from '@app/shared/services/utils.service';
import { wrapError } from '@app/utils/rxjs';
import { enhanceUsersWithExtension } from '@app/utils/user';
import { Observable } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class RoomGroupRepository {
  constructor(
    private http: HttpClient,
    private utils: Utils
  ) {}

  add(room: Partial<RoomGroup>): Observable<void> {
    return this.utils.apiUrl(`/rooms`).pipe(
      mergeMap((url) => this.http.post<void>(url, room)),
      wrapError('room-group-repository:add')
    );
  }

  addUserToRoom(roomId: number, userId: number): Observable<void> {
    const url = `/rooms/${roomId}/users/${userId}`;

    const urls = {
      [UserLevel.ServiceProviderAdmin]: BaseUrls.ServiceProvider.Enterprise + url,
      [UserLevel.EnterpriseAdmin]: BaseUrls.Enterprise.Enterprise + url,
    };

    return this.utils.apiUrl(urls).pipe(
      mergeMap((url) => this.http.put<void>(url, {})),
      wrapError('room-group-repository:assign-user')
    );
  }

  assignUser(roomId: number, userId: number): Observable<void> {
    return this.utils.apiUrl(`/rooms/${roomId}/users/${userId}`).pipe(
      mergeMap((url) => this.http.put<void>(url, {})),
      wrapError('room-group-repository:assign-user')
    );
  }

  delete(id: number): Observable<void> {
    return this.utils.apiUrl(`/rooms/${id}`).pipe(
      mergeMap((url) => this.http.delete<void>(url)),
      wrapError('room-group-repository:delete')
    );
  }

  get(id: number): Observable<RoomGroup> {
    return this.utils.apiUrl(`/rooms/${id}`).pipe(
      mergeMap((url) => this.http.get<RoomGroup>(url)),
      wrapError('room-group-repository:get')
    );
  }

  list(): Observable<RoomGroup[]> {
    return this.utils.apiUrl('/rooms').pipe(
      mergeMap((url) => this.http.get<RoomGroup[]>(url)),
      wrapError('room-group-repository:list')
    );
  }

  listEnterpriseRooms(): Observable<RoomGroup[]> {
    return this.utils.routeBasedApiUrl('/rooms', UserLevel.EnterpriseAdmin).pipe(
      mergeMap((url) => this.http.get<RoomGroup[]>(url)),
      wrapError('room-group-repository:list-enterprise-rooms')
    );
  }

  listUserRooms(): Observable<RoomGroup[]> {
    return this.utils.apiUrl('/rooms?loginName=:userId').pipe(
      mergeMap((url) => this.http.get<RoomGroup[]>(url)),
      wrapError('room-group-repository:list-user-rooms')
    );
  }

  listUsersOfRoom(id: number): Observable<EnhancedEndUser[]> {
    return this.utils.apiUrl(`/rooms/${id}/users`).pipe(
      mergeMap((url) => this.http.get<EndUserWithId[]>(url)),
      enhanceUsersWithExtension(),
      wrapError('room-group-repository:list-users-of-room')
    );
  }

  removeUserFromRoom(roomId: number, userId: number): Observable<void> {
    const url = `/rooms/${roomId}/users/${userId}`;

    const urls = {
      [UserLevel.ServiceProviderAdmin]: BaseUrls.ServiceProvider.Enterprise + url,
      [UserLevel.EnterpriseAdmin]: BaseUrls.Enterprise.Enterprise + url,
    };

    return this.utils.apiUrl(urls).pipe(
      mergeMap((url) => this.http.delete<void>(url, {})),
      wrapError('room-group-repository:unassign-user-from-room')
    );
  }

  unassignUser(roomId: number, userId: number): Observable<void> {
    return this.utils.apiUrl(`/rooms/${roomId}/users/${userId}`).pipe(
      mergeMap((url) => this.http.delete<void>(url)),
      wrapError('room-group-repository:unassign-user')
    );
  }

  update(room: RoomGroup): Observable<void> {
    return this.utils.apiUrl(`/rooms/${room.id}`).pipe(
      mergeMap((url) => this.http.put<void>(url, room)),
      wrapError('room-group-repository:update')
    );
  }
}
